import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/pt-br";
import {
  Col,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import SimpleBar from "simplebar-react";

import bell from "../../assets/images/svg/bell.svg";
import {
  AppNotification,
  isNotificationUnread,
} from "../../services/notificationService";
import {
  listAdminNotifications,
  markAllAdminNotificationsAsRead,
  markAdminNotificationAsRead,
} from "../../services/superAdmin/adminNotificationService";

const NOTIFICATIONS_PER_PAGE = 20;

const resolveMomentLocale = (language: string): string => {
  if (language === "pt-BR") {
    return "pt-br";
  }

  if (language === "es") {
    return "es";
  }

  return "en";
};

const SuperAdminNotificationDropdown = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [markingIds, setMarkingIds] = useState<Set<string>>(new Set());

  const unreadCount = useMemo(
    () => notifications.filter((item) => isNotificationUnread(item)).length,
    [notifications]
  );

  const fetchNotifications = useCallback(async () => {
    setLoading(true);

    try {
      const response = await listAdminNotifications({
        per_page: NOTIFICATIONS_PER_PAGE,
      });
      setNotifications(response.data || []);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchNotifications();
    const intervalId = window.setInterval(() => {
      void fetchNotifications();
    }, 60_000);

    return () => window.clearInterval(intervalId);
  }, [fetchNotifications]);

  useEffect(() => {
    if (open) {
      void fetchNotifications();
    }
  }, [open, fetchNotifications]);

  const formatRelativeTime = (createdAt: string): string => {
    moment.locale(resolveMomentLocale(i18n.language));

    return moment(createdAt).fromNow();
  };

  const resolveTitle = (notification: AppNotification): string => {
    const data = notification.data || {};

    if (typeof data.title === "string" && data.title.trim() !== "") {
      return data.title;
    }

    if (typeof data.title_key === "string") {
      return t(data.title_key, data);
    }

    return t("header.notifications.fallback_title");
  };

  const resolveBody = (notification: AppNotification): string => {
    const data = notification.data || {};

    if (typeof data.body === "string") {
      return data.body;
    }

    if (typeof data.body_key === "string") {
      return t(data.body_key, data);
    }

    return "";
  };

  const handleMarkAsRead = async (notification: AppNotification): Promise<void> => {
    if (!isNotificationUnread(notification) || markingIds.has(notification.id)) {
      return;
    }

    setMarkingIds((previous) => new Set(previous).add(notification.id));

    try {
      const response = await markAdminNotificationAsRead(notification.id);
      setNotifications((previous) =>
        previous.map((item) =>
          item.id === notification.id ? response.data : item
        )
      );
    } catch {
      // api_helper surfaces errors when applicable
    } finally {
      setMarkingIds((previous) => {
        const next = new Set(previous);
        next.delete(notification.id);
        return next;
      });
    }
  };

  const handleClick = async (notification: AppNotification): Promise<void> => {
    if (isNotificationUnread(notification)) {
      await handleMarkAsRead(notification);
    }

    const actionUrl =
      typeof notification.data?.action_url === "string"
        ? notification.data.action_url.trim()
        : "/super-admin/support/tickets";

    setOpen(false);
    navigate(actionUrl);
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0 || markingAll) {
      return;
    }

    setMarkingAll(true);

    try {
      await markAllAdminNotificationsAsRead();
      const readAt = new Date().toISOString();
      setNotifications((previous) =>
        previous.map((item) => ({
          ...item,
          read_at: item.read_at ?? readAt,
        }))
      );
    } catch {
      // ignore
    } finally {
      setMarkingAll(false);
    }
  };

  return (
    <Dropdown
      isOpen={open}
      toggle={() => setOpen((current) => !current)}
      className="topbar-head-dropdown ms-1 header-item"
    >
      <DropdownToggle
        type="button"
        tag="button"
        className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        aria-label={t("header.notifications.title")}
      >
        <i className="bx bx-bell fs-22" />
        {unreadCount > 0 ? (
          <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
        <div className="dropdown-head bg-primary bg-pattern rounded-top">
          <div className="p-3">
            <Row className="align-items-center">
              <Col>
                <h6 className="m-0 fs-16 fw-semibold text-white">
                  {t("header.notifications.title")}
                </h6>
              </Col>
              {unreadCount > 0 ? (
                <Col xs="auto">
                  <span className="badge bg-light-subtle fs-13 text-body">
                    {t("header.notifications.unread_summary", {
                      count: unreadCount,
                    })}
                  </span>
                </Col>
              ) : null}
            </Row>
          </div>
        </div>

        <div className="py-2 ps-2">
          {loading && notifications.length === 0 ? (
            <div className="py-4 text-center text-muted">
              <Spinner size="sm" className="me-2" />
              {t("header.notifications.loading")}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-4 text-center">
              <img src={bell} className="img-fluid w-25 mx-auto d-block" alt="" />
              <h6 className="fs-18 fw-semibold lh-base mt-2">
                {t("header.notifications.empty")}
              </h6>
            </div>
          ) : (
            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
              {notifications.map((notification) => {
                const isUnread = isNotificationUnread(notification);

                return (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    className={classnames(
                      "text-reset notification-item d-block dropdown-item",
                      { active: isUnread }
                    )}
                    onClick={() => void handleClick(notification)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        void handleClick(notification);
                      }
                    }}
                  >
                    <div className="d-flex">
                      <div className="avatar-xs me-3 flex-shrink-0">
                        <span className="avatar-title rounded-circle fs-16 bg-primary-subtle text-primary">
                          <i className="bx bx-headphone" />
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="mt-0 mb-1 fs-13 fw-semibold">
                          {resolveTitle(notification)}
                        </h6>
                        <p className="mb-1 fs-13 text-muted">
                          {resolveBody(notification)}
                        </p>
                        <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                          <i className="mdi mdi-clock-outline" />{" "}
                          {formatRelativeTime(notification.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}

              {unreadCount > 0 ? (
                <div className="my-3 text-center">
                  <button
                    type="button"
                    className="btn btn-soft-success"
                    disabled={markingAll}
                    onClick={(event) => {
                      event.stopPropagation();
                      void handleMarkAllAsRead();
                    }}
                  >
                    {markingAll ? (
                      <Spinner size="sm" />
                    ) : (
                      t("header.notifications.mark_all_read")
                    )}
                  </button>
                </div>
              ) : null}
            </SimpleBar>
          )}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default SuperAdminNotificationDropdown;
